/**
 * Quick checkout (/:clusterId/quick-checkout) reuses the product link page and
 * its payment handlers. These helpers adapt the product-link request and
 * validate the customer-entered amount.
 */

/**
 * Turns a product-link payment request into a quick-checkout one: no product
 * link or upsell, and no lead or cluster (the cluster comes from the URL
 * slug). A logged-in customer's Account ID is sent so the payment goes to
 * their account (the API checks it against the login); a guest sends none and
 * the API matches the customer by email.
 */
export const toQuickCheckoutPayload = <T extends Record<string, any>>(
  payload: T,
  loggedInAccountId?: string | null
) => {
  const {
    product_link,
    isUpsellEnabled,
    accountid,
    lead_id,
    cluster_id,
    ...rest
  } = payload;

  return {
    ...rest,
    ...(loggedInAccountId ? { accountid: loggedInAccountId } : {}),
    description: "Online Payment",
    payment_note: "One-time",
  };
};

/**
 * The quick checkout API always applies the convenience fee
 * (custom_conv 'on'); fee lookups and intents must use the same setting so
 * the fee shown is the fee charged.
 */
export const QUICK_CHECKOUT_CUSTOM_CONV = "on";

/**
 * Return URL for redirect payments (Cash App, Amazon Pay, Stripe bank,
 * PayPal): the current page path plus encoded params, so an existing query
 * string is not repeated and values like "a+b@x.com" survive the round trip.
 */
export const buildQuickCheckoutReturnUrl = (
  params: Record<string, string | number | boolean | undefined | null>,
  location: { origin: string; pathname: string } = window.location
): string => {
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) query.set(key, String(value));
  });
  return `${location.origin}${location.pathname}?${query.toString()}`;
};

/**
 * State fee rules are stored by 2-letter US state code, while the billing
 * form holds the state's name. Returns the code of the selected US billing
 * state for fee lookups and intents, or "" when there is none (the cluster's
 * default fee then applies).
 */
export const getQuickCheckoutFeeState = (
  country: unknown,
  stateName: unknown,
  selectedState: { name?: string; state_code?: string } | null | undefined
): string => {
  if (String(country ?? "").toUpperCase() !== "US") return "";
  if (!stateName || selectedState?.name !== stateName) return "";
  const code = String(selectedState?.state_code ?? "").toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
};

/** Smallest amount Stripe and the quick checkout API accept. */
export const QUICK_CHECKOUT_MIN_AMOUNT = 1;

/** True when `amount` is at least $1.00 with at most two decimals. */
export const isValidQuickCheckoutAmount = (amount: unknown): boolean => {
  if (amount === null || amount === undefined || amount === "") return false;
  const text = String(amount).trim();
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return false;
  return Number(text) >= QUICK_CHECKOUT_MIN_AMOUNT;
};

/**
 * True when the portal API's `quick_checkout_enabled` says quick checkout is
 * switched on for the cluster (account_settings.quick_checkout = 'Y').
 * Anything but `true` means off.
 */
export const isQuickCheckoutEnabled = (apiEnabled: unknown): boolean =>
  apiEnabled === true;

/**
 * The PayPal order quick checkout waits on while the customer is at PayPal.
 * Kept in this tab's sessionStorage (gone when the tab closes), not in the
 * app's persisted store, which other pages use for their own PayPal orders.
 */
export type QuickCheckoutPayPalOrder = {
  url?: string;
  token?: string;
  xEmail?: string;
  amount?: string | number;
};
const PAYPAL_ORDER_KEY = "qc:paypal-order";
export const saveQuickCheckoutPayPalOrder = (
  order: QuickCheckoutPayPalOrder | null
): void => {
  try {
    if (order) sessionStorage.setItem(PAYPAL_ORDER_KEY, JSON.stringify(order));
    else sessionStorage.removeItem(PAYPAL_ORDER_KEY);
  } catch (error) {
    /* storage unavailable: the return then asks the customer to start over */
  }
};
export const readQuickCheckoutPayPalOrder =
  (): QuickCheckoutPayPalOrder | null => {
    try {
      const text = sessionStorage.getItem(PAYPAL_ORDER_KEY);
      const order = text ? JSON.parse(text) : null;
      return order && typeof order === "object" ? order : null;
    } catch (error) {
      return null;
    }
  };

/**
 * The payer's email for a redirect payment (Cash App, Amazon Pay, Stripe
 * bank), which the API needs to record the payment Stripe has taken. It is
 * kept out of the return URL (Stripe, browser history and server logs see
 * that) and held in this browser for a short while instead. localStorage,
 * not sessionStorage: the Cash App app can return to a new tab.
 */
const RETURN_EMAIL_KEY = "qc:return-email";
const RETURN_EMAIL_TTL_MS = 2 * 60 * 60 * 1000;
export const rememberQuickCheckoutReturnEmail = (
  email: string | null | undefined,
  now: number = Date.now()
): void => {
  try {
    if (email) {
      localStorage.setItem(RETURN_EMAIL_KEY, JSON.stringify({ email, at: now }));
    }
  } catch (error) {
    /* storage unavailable */
  }
};
export const takeQuickCheckoutReturnEmail = (
  now: number = Date.now()
): string => {
  try {
    const saved = JSON.parse(localStorage.getItem(RETURN_EMAIL_KEY) || "null");
    localStorage.removeItem(RETURN_EMAIL_KEY);
    return saved?.email && now - Number(saved.at) < RETURN_EMAIL_TTL_MS
      ? String(saved.email)
      : "";
  } catch (error) {
    return "";
  }
};

/** What the customer sees when a payment may have gone through. */
export const QUICK_CHECKOUT_CONFIRMING_MESSAGE =
  "Your payment was received and is being confirmed. Please check your email for the receipt before trying again.";

/** True when the API (or runQuickCheckoutPayment) says "being confirmed". */
export const isQuickCheckoutPendingReply = (res: any): boolean =>
  Boolean(res?.body?.data?.pending);

/**
 * The reason shown for a failed payment: the first non-empty one of the
 * fields the API may fill (an empty gateway_error must not hide the message).
 */
export const quickCheckoutFailureReason = (res: any): string =>
  [
    res?.body?.data?.gateway_error,
    res?.body?.data?.message,
    res?.body?.message,
    res?.body?.msg,
  ].find((value) => typeof value === "string" && value.trim()) ||
  "Payment failed";

const wait = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Sends a quick checkout payment and never turns a payment that may have
 * gone through into "failed":
 *  - no answer, a 5xx or a 409 ("still being processed"): when the request is
 *    safe to repeat (`canRetry`, a Stripe intent the API recognises), it is
 *    sent again (same body) after each delay; otherwise, or when the retries
 *    run out, the result is a "being confirmed" reply
 *  - any other 4xx: a failed reply carrying the API's message (no order, so
 *    no failed-payment email)
 * Answers the API gave are returned as they are.
 */
export const runQuickCheckoutPayment = async (
  send: (payload: any) => Promise<any>,
  payload: any,
  {
    canRetry = false,
    delaysMs = [1500, 3000],
    pause = wait,
  }: {
    canRetry?: boolean;
    delaysMs?: number[];
    pause?: (ms: number) => Promise<void>;
  } = {}
): Promise<any> => {
  for (let attempt = 0; ; attempt += 1) {
    try {
      return await send(payload);
    } catch (error: any) {
      if (error?.name === "CanceledError" || error?.name === "AbortError") {
        throw error;
      }
      const status = Number(error?.response?.status) || 0;
      const unclear = status === 0 || status === 409 || status >= 500;
      if (unclear) {
        if (canRetry && attempt < delaysMs.length) {
          await pause(delaysMs[attempt]);
          continue;
        }
        return {
          body: {
            success: false,
            data: { pending: true, message: QUICK_CHECKOUT_CONFIRMING_MESSAGE },
          },
        };
      }
      const answer = error?.response?.data;
      const message = [
        answer?.body?.message,
        answer?.body?.data?.msg,
        answer?.message,
      ].find((value) => typeof value === "string" && value.trim());
      return {
        body: {
          success: false,
          data: { message: message || "Payment failed, please try again." },
        },
      };
    }
  }
};

/**
 * A new attempt id for make-quick-checkout-payment (8-64 letters, digits or
 * dashes, as the API accepts).
 */
export const newQuickCheckoutAttemptId = (): string => {
  const cryptoApi =
    typeof globalThis !== "undefined" ? (globalThis as any).crypto : undefined;
  if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();
  return `qc-${Date.now().toString(36)}-${Math.random()
    .toString(36)
    .slice(2, 12)}`;
};

/**
 * Payment methods whose form is tied to the amount it was opened with: a
 * Stripe intent (Cash App Pay, Amazon Pay, Stripe bank) or a Plaid link.
 */
export const QUICK_CHECKOUT_INTENT_BOUND_METHODS: readonly string[] = [
  "Bank",
  "Cash App Pay",
  "Amazon Pay",
];

/**
 * True on /:clusterId/quick-checkout. The shared payment components create
 * Stripe intents / PayPal tokens through common services; on this page those
 * services switch to the guest-safe quick checkout endpoints.
 */
export const isQuickCheckoutRoute = (
  pathname: string = typeof window !== "undefined"
    ? window.location.pathname
    : ""
): boolean => pathname.split("/").filter(Boolean)[1] === "quick-checkout";
